import { ProductJourney } from "@shared/analytics/productEvents";
import { API_URLS, type YaasWalletResponse } from "@solomon-apps/shared";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { observeSettingsVerification } from "@/services/analytics/settings";

import { yaasWalletQueryKey } from "./queryKeys";
import { useWalletVerification } from "./useWalletVerification";
import { requestWalletActionChallenge, submitWithVerificationRetry } from "./verificationRequests";
import { mergeWalletViews } from "./walletView";

export function rewardsDestinationUrl(apiBase: string, walletAddress: string): string {
    return `${apiBase}/api/yaas/wallet/${walletAddress}/rewards-destination`;
}

export function useRewardsDestination(): {
    setRewardsDestination: (input: {
        walletAddress: string;
        analytics?: ProductJourney;
        rewardsDestinationAddress: string;
    }) => Promise<{ success: true; data: YaasWalletResponse } | { success: false; error: string }>;
    isLoading: boolean;
} {
    const queryClient = useQueryClient();
    const verification = useWalletVerification();
    const [isLoading, setIsLoading] = useState(false);

    const setRewardsDestination = async (input: {
        walletAddress: string;
        analytics?: ProductJourney;
        rewardsDestinationAddress: string;
    }): Promise<{ success: true; data: YaasWalletResponse } | { success: false; error: string }> => {
        const analytics =
            input.analytics ?? new ProductJourney("rewards_destination", "position_row", input.walletAddress);
        const attempt = analytics.attempt({
            destination_kind: input.rewardsDestinationAddress === input.walletAddress ? "self" : "custom"
        });
        attempt.send("challenge_started");
        setIsLoading(true);
        try {
            const url = rewardsDestinationUrl(API_URLS.YAAS, input.walletAddress);
            const challenge = await requestWalletActionChallenge(
                `${url}/challenge`,
                { rewardsDestinationAddress: input.rewardsDestinationAddress },
                "Unable to request verification"
            );
            attempt.challengeCompleted();
            const proof = await verification.verify({
                purpose: "action",
                analytics: observeSettingsVerification(attempt),
                walletAddress: input.walletAddress,
                message: challenge.message,
                nonce: challenge.nonce,
                expiresAt: challenge.expiresAt,
                microTransfer: challenge.microTransfer
            });
            attempt.submit(proof.method);
            const response = await submitWithVerificationRetry(
                url,
                {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        rewardsDestinationAddress: input.rewardsDestinationAddress,
                        nonce: challenge.nonce,
                        proof
                    })
                },
                challenge.expiresAt
            );
            if (!response.ok) {
                const body = (await response.json().catch(() => ({}))) as { error?: string };
                throw new Error(body.error ?? `Unable to update rewards destination (${response.status})`);
            }
            const data = (await response.json()) as YaasWalletResponse;
            attempt.complete();
            queryClient.setQueryData<YaasWalletResponse>(yaasWalletQueryKey(input.walletAddress), (previous) =>
                mergeWalletViews(previous, data)
            );
            return { success: true, data };
        } catch (error) {
            attempt.fail(error);
            return {
                success: false,
                error: error instanceof Error ? error.message : "Unable to update rewards destination"
            };
        } finally {
            setIsLoading(false);
        }
    };
    return { setRewardsDestination, isLoading };
}
